import {FC, useCallback, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Box, Flex, IconButton, Text} from '@chakra-ui/react';
import {getDateTimeString} from '@progress-fe/core';
import {Dot, Svg} from '@progress-fe/ui-kit';
import {IJournalPoint} from 'core/interfaces';
import {ECalculationStatus, EDumpReasonType} from 'core/enums';

import {CreateCheckpointDialog} from './components';

interface IProps {
  points: IJournalPoint[];
  activeNumber: number;
  onSelectNumber: (newActiveNumber: number) => void;
  onCreateCheckpointByCalcNumber: (calcNumber: number, reason: EDumpReasonType) => Promise<void>;
}

const JournalPointListFC: FC<IProps> = ({
  points,
  activeNumber,
  onSelectNumber,
  onCreateCheckpointByCalcNumber
}) => {
  const [pointToCreate, setPointToCreate] = useState<IJournalPoint | null>(null);

  const getColor = useCallback((point: IJournalPoint) => {
    switch (point.status) {
      case ECalculationStatus.CalcResultSuccess:
        return 'success';
      case ECalculationStatus.CalcResultPartialSuccess:
        return 'darkOrange';
      case ECalculationStatus.CalcResultError:
        return 'darkRed';
      default:
        return 'black';
    }
  }, []);

  const hasWarningDot = useCallback((point: IJournalPoint) => {
    return point.status === ECalculationStatus.CalcResultSuccess && point.isWarning;
  }, []);

  return (
    <Box>
      {points.map((point) => (
        <Box
          p={'2px 4px'}
          role={'group'}
          color={getColor(point)}
          key={point.calculationNumber}
          bg={activeNumber === point.calculationNumber ? 'buttonBg' : 'transparent'}
          _hover={{bg: 'buttonBg', cursor: 'pointer'}}
          onClick={() => onSelectNumber(point.calculationNumber)}
        >
          <Flex gap={'4px'} alignItems={'center'} justifyContent={'space-between'}>
            <Flex gap={'4px'} alignItems={'center'}>
              <Dot variant={hasWarningDot(point) ? 'warning' : 'transparent'} />
              <Text as={'span'} fontSize={'12px'} fontWeight={600} color={'inherit'}>
                №{point.calculationNumber}:{' '}
              </Text>
              <Box>{getDateTimeString(point.runAt)}</Box>
            </Flex>

            <Box opacity={0} _groupHover={{opacity: 1}}>
              <IconButton
                size="xs"
                aria-label=""
                variant="ghost"
                icon={<Svg name={'Plus'} size={'s12'} />}
                onClick={() => setPointToCreate(point)}
              />
            </Box>
          </Flex>
        </Box>
      ))}

      {/* CREATE CHECKPOINT DIALOG */}
      {pointToCreate && (
        <CreateCheckpointDialog
          point={pointToCreate}
          onCreate={onCreateCheckpointByCalcNumber}
          onClose={() => setPointToCreate(null)}
        />
      )}
    </Box>
  );
};

export const JournalPointList = observer(JournalPointListFC);
